import { FC, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { useDispatch } from "react-redux";

import { Input, Label, Button } from "../../common";
import { FormErrorMessage } from "../../messages";
import { ONBOARDING_SURVEY_ID, PHONE_CHANNEL_LABELS } from "../../../constants";
import { ROUTES } from "../../../routes";
import {
  useConfirmPhoneMutation,
  useUpdatePhoneMutation,
} from "../../../store/services/user";
import { updatePhoneState } from "../../../store/reducers/user";
import { toastSuccess } from "../../../utils/toasts";
import { PhoneChannel } from "../../../types/UserTypes";

type Inputs = {
  token: string;
};

export const ConfirmNumberForm: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    handleSubmit,
    control,
    formState: { isDirty, isValid },
  } = useForm<Inputs>({
    defaultValues: {
      token: "",
    },
  });

  const [confirmPhone, { isSuccess, isLoading, isError, error }] =
    useConfirmPhoneMutation();

  const [updatePhone, { isSuccess: isResend }] = useUpdatePhoneMutation();

  const onSubmit: SubmitHandler<Inputs> = async (data) =>
    await confirmPhone(data);

  const phoneValue = sessionStorage.getItem("phoneNumber") || "";

  const phoneChannel =
    (sessionStorage.getItem("phoneChannel") as PhoneChannel) ||
    PhoneChannel.SMS;

  const onResendCode = useCallback(async () => {
    await updatePhone({
      phone: phoneValue,
      channel: phoneChannel,
    });
  }, [phoneChannel, phoneValue, updatePhone]);

  const onTryADifferentChannel = useCallback(() => {
    navigate(ROUTES.AUTH_ADD_NUMBER);
  }, [navigate]);

  useEffect(() => {
    if (!phoneValue) {
      navigate(ROUTES.AUTH_LOGIN);
    }
  }, [navigate, phoneValue]);

  useEffect(() => {
    if (isSuccess) {
      dispatch(updatePhoneState(phoneValue));

      if (!ONBOARDING_SURVEY_ID) {
        toastSuccess("Your account is ready!");
      }
    }
  }, [isLoading, isSuccess, navigate, phoneValue, dispatch]);

  return (
    <div className="mt-8">
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
        <Label
          label={`6-digit code from ${PHONE_CHANNEL_LABELS[phoneChannel]}`}
        >
          <Controller
            name="token"
            control={control}
            rules={{ required: true }}
            render={({ field }) => <Input type="text" autoFocus {...field} />}
          />
        </Label>

        <FormErrorMessage isError={isError} error={error} />

        <Button
          type="submit"
          variant={Button.variant.PRIMARY}
          className="w-full"
          disabled={!isDirty || !isValid}
          data-test="confirm-btn"
        >
          Confirm
        </Button>

        <div className="flex flex-col pt-6">
          {phoneChannel === PhoneChannel.SMS && (
            <div className="text-sm text-gray-300 text-center">
              SMS can take several minutes to arrive in some countries.
            </div>
          )}

          <Button
            variant={Button.variant.TERTIARY}
            className="w-full"
            disabled={isResend}
            onClick={onResendCode}
            data-test={isResend ? "sent-btn" : "resend-code-btn"}
          >
            {isResend ? "Sent" : "Resend code"}
          </Button>

          <div className="text-sm text-gray-300 text-center">Or</div>

          <Button
            variant={Button.variant.TERTIARY}
            className="w-full"
            onClick={onTryADifferentChannel}
            data-test={"try-different-channel-btn"}
          >
            Try a different channel
          </Button>
        </div>

        <div className="flex flex-col">
          <div className="text-sm	text-gray-300 text-center">
            Not receiving the message code?
          </div>

          <Button
            variant={Button.variant.TERTIARY}
            className="w-full contact-support-btn"
            data-test="contact-support-btn"
          >
            Contact support
          </Button>

          <div className="text-sm	text-gray-300 text-center">
            To get assistance with verifying your phone
          </div>
        </div>
      </form>
    </div>
  );
};
